/**
 * Scheduled Jobs Manager
 *
 * Admin UI for configuring, controlling, and monitoring scheduled jobs.
 * Supports both local node-cron (development) and Google Cloud Scheduler (production).
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Stop as StopIcon,
  Refresh as RefreshIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  History as HistoryIcon,
  Sync as SyncIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  ExpandMore as ExpandMoreIcon,
  Cloud as CloudIcon,
  Computer as LocalIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { rawApiClient as apiClient } from '../../services/api';

const API_BASE = '/scheduled-jobs';

// Status chip colors
const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'running': return 'info';
    case 'failed': return 'error';
    case 'timeout': return 'warning';
    case 'pending': return 'default';
    case 'cancelled': return 'default';
    default: return 'default';
  }
};

const getSyncStatusColor = (status) => {
  switch (status) {
    case 'synced': return 'success';
    case 'pending': return 'warning';
    case 'error': return 'error';
    case 'not_applicable': return 'default';
    default: return 'default';
  }
};

// Tab panel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`jobs-tabpanel-${index}`}
      aria-labelledby={`jobs-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ScheduledJobsManager = () => {
  const [tabValue, setTabValue] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [systemStatus, setSystemStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Dialog states
  const [jobDialogOpen, setJobDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  // Selected job for operations
  const [selectedJob, setSelectedJob] = useState(null);
  const [jobHistory, setJobHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Form state for new/edit job
  const [jobForm, setJobForm] = useState({
    jobKey: '',
    name: '',
    description: '',
    category: 'other',
    schedule: {
      cronExpression: '',
      timezone: 'UTC',
      humanReadable: ''
    },
    endpoint: {
      uri: '',
      method: 'POST',
      body: {}
    },
    settings: {
      timeout: 300000,
      retryOnFailure: false,
      maxRetries: 3,
      notifyOnFailure: true,
      notifyOnSuccess: false
    }
  });

  // Schedule form state
  const [scheduleForm, setScheduleForm] = useState({
    cronExpression: '',
    timezone: 'UTC',
    humanReadable: ''
  });

  // Confirm dialog state
  const [confirmAction, setConfirmAction] = useState({ type: '', job: null });

  // Fetch system status
  const fetchSystemStatus = useCallback(async () => {
    try {
      const response = await apiClient.get(`${API_BASE}/status`);
      setSystemStatus(response.data);
    } catch (err) {
      console.error('Error fetching system status:', err);
    }
  }, []);

  // Fetch all jobs
  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const response = await apiClient.get(API_BASE);
      setJobs(response.data.jobs || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch jobs');
      console.error('Error fetching jobs:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Initialize on mount
  useEffect(() => {
    fetchSystemStatus();
    fetchJobs();
  }, [fetchSystemStatus, fetchJobs]);

  // Clear messages after delay
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 5000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // Fetch job history
  const fetchJobHistory = async (jobKey) => {
    try {
      setHistoryLoading(true);
      const response = await apiClient.get(`${API_BASE}/${jobKey}/history`);
      setJobHistory(response.data.runs || []);
    } catch (err) {
      console.error('Error fetching job history:', err);
      setError(err.response?.data?.error || 'Failed to fetch job history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Job actions
  const handleTriggerJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/trigger`);
      setSuccess(response.data.message || 'Job triggered successfully');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to trigger job');
    }
  };

  const handleEnableJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/enable`);
      setSuccess(response.data.message || 'Job enabled');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to enable job');
    }
  };

  const handleDisableJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/disable`);
      setSuccess(response.data.message || 'Job disabled');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to disable job');
    }
  };

  const handlePauseJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/pause`);
      setSuccess(response.data.message || 'Job paused');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to pause job');
    }
  };

  const handleResumeJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/resume`);
      setSuccess(response.data.message || 'Job resumed');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to resume job');
    }
  };

  const handleDeleteJob = async (jobKey) => {
    try {
      await apiClient.delete(`${API_BASE}/${jobKey}`);
      setSuccess('Job deleted successfully');
      setConfirmDialogOpen(false);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete job');
    }
  };

  const handleSyncJob = async (jobKey) => {
    try {
      const response = await apiClient.post(`${API_BASE}/${jobKey}/sync`);
      setSuccess(response.data.message || 'Job synced to Cloud Scheduler');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync job');
    }
  };

  const handleSyncAllJobs = async () => {
    try {
      const response = await apiClient.post(`${API_BASE}/sync-all`);
      setSuccess(`Synced ${response.data.results?.length || 0} jobs to Cloud Scheduler`);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to sync jobs');
    }
  };

  const handleInitializeDefaults = async () => {
    try {
      const response = await apiClient.post(`${API_BASE}/initialize`);
      setSuccess(response.data.message || 'Default jobs initialized');
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to initialize default jobs');
    }
  };

  // Update schedule
  const handleUpdateSchedule = async () => {
    if (!selectedJob) return;
    try {
      const response = await apiClient.put(`${API_BASE}/${selectedJob.jobKey}/schedule`, scheduleForm);
      setSuccess(response.data.message || 'Schedule updated');
      setScheduleDialogOpen(false);
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update schedule');
    }
  };

  // Create/Update job
  const handleSaveJob = async () => {
    try {
      if (selectedJob) {
        // Update existing job
        const response = await apiClient.put(`${API_BASE}/${selectedJob.jobKey}`, jobForm);
        setSuccess(response.data.message || 'Job updated successfully');
      } else {
        // Create new job
        const response = await apiClient.post(API_BASE, jobForm);
        setSuccess(response.data.message || 'Job created successfully');
      }
      setJobDialogOpen(false);
      setSelectedJob(null);
      resetJobForm();
      fetchJobs();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save job');
    }
  };

  const resetJobForm = () => {
    setJobForm({
      jobKey: '',
      name: '',
      description: '',
      category: 'other',
      schedule: {
        cronExpression: '',
        timezone: 'UTC',
        humanReadable: ''
      },
      endpoint: {
        uri: '',
        method: 'POST',
        body: {}
      },
      settings: {
        timeout: 300000,
        retryOnFailure: false,
        maxRetries: 3,
        notifyOnFailure: true,
        notifyOnSuccess: false
      }
    });
  };

  // Open dialogs
  const openEditDialog = (job) => {
    setSelectedJob(job);
    setJobForm({
      jobKey: job.jobKey,
      name: job.name,
      description: job.description || '',
      category: job.category || 'other',
      schedule: job.schedule || { cronExpression: '', timezone: 'UTC' },
      endpoint: job.endpoint || { uri: '', method: 'POST', body: {} },
      settings: job.settings || {}
    });
    setJobDialogOpen(true);
  };

  const openCreateDialog = () => {
    setSelectedJob(null);
    resetJobForm();
    setJobDialogOpen(true);
  };

  const openHistoryDialog = (job) => {
    setSelectedJob(job);
    fetchJobHistory(job.jobKey);
    setHistoryDialogOpen(true);
  };

  const openScheduleDialog = (job) => {
    setSelectedJob(job);
    setScheduleForm({
      cronExpression: job.schedule?.cronExpression || '',
      timezone: job.schedule?.timezone || 'UTC',
      humanReadable: job.schedule?.humanReadable || ''
    });
    setScheduleDialogOpen(true);
  };

  const openConfirmDialog = (type, job) => {
    setConfirmAction({ type, job });
    setConfirmDialogOpen(true);
  };

  // Format date for display
  const formatDate = (date) => {
    if (!date) return 'Never';
    return new Date(date).toLocaleString();
  };

  // Format duration
  const formatDuration = (ms) => {
    if (!ms) return '-';
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
    return `${(ms / 60000).toFixed(1)}m`;
  };

  // Render system status card
  const renderSystemStatus = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          System Status
        </Typography>
        {systemStatus ? (
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {systemStatus.isCloudRun ? <CloudIcon color="primary" /> : <LocalIcon color="secondary" />}
                <Typography variant="body2">
                  <strong>Environment:</strong> {systemStatus.isCloudRun ? 'Cloud Run (Production)' : 'Local Development'}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2">
                <strong>Scheduler Mode:</strong> {systemStatus.mode || 'Unknown'}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Typography variant="body2">
                <strong>Total Jobs:</strong> {systemStatus.totalJobs || 0}
              </Typography>
            </Grid>
            <Grid item xs={12} md={3}>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Chip
                  size="small"
                  label={`${systemStatus.enabledJobs || 0} Enabled`}
                  color="success"
                  variant="outlined"
                />
                <Chip
                  size="small"
                  label={`${systemStatus.pausedJobs || 0} Paused`}
                  color="warning"
                  variant="outlined"
                />
              </Box>
            </Grid>
          </Grid>
        ) : (
          <CircularProgress size={20} />
        )}
      </CardContent>
    </Card>
  );

  // Render jobs table
  const renderJobsTable = () => (
    <TableContainer component={Paper}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Job</TableCell>
            <TableCell>Category</TableCell>
            <TableCell>Schedule</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Last Run</TableCell>
            <TableCell>Next Run</TableCell>
            <TableCell>Stats</TableCell>
            <TableCell>Sync</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {jobs.length === 0 ? (
            <TableRow>
              <TableCell colSpan={9} align="center">
                <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                  No scheduled jobs configured. Click "Add Job" to create one, or "Initialize Defaults" to set up standard jobs.
                </Typography>
              </TableCell>
            </TableRow>
          ) : (
            jobs.map((job) => (
              <TableRow key={job.jobKey} sx={{ opacity: job.isEnabled ? 1 : 0.6 }}>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {job.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {job.jobKey}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip label={job.category || 'other'} size="small" variant="outlined" />
                </TableCell>
                <TableCell>
                  <Tooltip title={job.schedule?.cronExpression || 'Not set'}>
                    <Typography variant="body2">
                      {job.schedule?.humanReadable || job.schedule?.cronExpression || 'Not set'}
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {job.isEnabled ? (
                      job.isPaused ? (
                        <Chip label="Paused" size="small" color="warning" />
                      ) : (
                        <Chip label="Active" size="small" color="success" />
                      )
                    ) : (
                      <Chip label="Disabled" size="small" color="default" />
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(job.stats?.lastRunAt)}
                  </Typography>
                  {job.stats?.lastRunDurationMs && (
                    <Typography variant="caption" color="text.secondary">
                      ({formatDuration(job.stats.lastRunDurationMs)})
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(job.stats?.nextScheduledRun)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Tooltip title={`${job.stats?.successfulRuns || 0} successful, ${job.stats?.failedRuns || 0} failed`}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <CheckCircleIcon color="success" fontSize="small" />
                      <Typography variant="body2">{job.stats?.successfulRuns || 0}</Typography>
                      <ErrorIcon color="error" fontSize="small" sx={{ ml: 1 }} />
                      <Typography variant="body2">{job.stats?.failedRuns || 0}</Typography>
                    </Box>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Chip
                    label={job.cloudScheduler?.syncStatus || 'N/A'}
                    size="small"
                    color={getSyncStatusColor(job.cloudScheduler?.syncStatus)}
                    variant="outlined"
                  />
                </TableCell>
                <TableCell align="right">
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                    <Tooltip title="Trigger Now">
                      <IconButton size="small" onClick={() => handleTriggerJob(job.jobKey)} disabled={!job.isEnabled}>
                        <PlayIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {job.isEnabled && !job.isPaused ? (
                      <Tooltip title="Pause">
                        <IconButton size="small" onClick={() => handlePauseJob(job.jobKey)}>
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : job.isPaused ? (
                      <Tooltip title="Resume">
                        <IconButton size="small" onClick={() => handleResumeJob(job.jobKey)}>
                          <PlayIcon fontSize="small" color="primary" />
                        </IconButton>
                      </Tooltip>
                    ) : null}
                    <Tooltip title={job.isEnabled ? 'Disable' : 'Enable'}>
                      <IconButton
                        size="small"
                        onClick={() => job.isEnabled ? handleDisableJob(job.jobKey) : handleEnableJob(job.jobKey)}
                      >
                        <StopIcon fontSize="small" color={job.isEnabled ? 'error' : 'success'} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Schedule">
                      <IconButton size="small" onClick={() => openScheduleDialog(job)}>
                        <ScheduleIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="View History">
                      <IconButton size="small" onClick={() => openHistoryDialog(job)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => openEditDialog(job)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Sync to Cloud Scheduler">
                      <IconButton size="small" onClick={() => handleSyncJob(job.jobKey)}>
                        <SyncIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => openConfirmDialog('delete', job)}>
                        <DeleteIcon fontSize="small" color="error" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Render job form dialog
  const renderJobDialog = () => (
    <Dialog open={jobDialogOpen} onClose={() => setJobDialogOpen(false)} maxWidth="md" fullWidth>
      <DialogTitle>{selectedJob ? 'Edit Job' : 'Create Job'}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12} md={6}>
            <TextField
              label="Job Key"
              value={jobForm.jobKey}
              onChange={(e) => setJobForm({ ...jobForm, jobKey: e.target.value })}
              fullWidth
              disabled={!!selectedJob}
              helperText="Unique identifier (e.g., 'daily-cleanup')"
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              label="Name"
              value={jobForm.name}
              onChange={(e) => setJobForm({ ...jobForm, name: e.target.value })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              value={jobForm.description}
              onChange={(e) => setJobForm({ ...jobForm, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Category</InputLabel>
              <Select
                value={jobForm.category}
                onChange={(e) => setJobForm({ ...jobForm, category: e.target.value })}
                label="Category"
              >
                <MenuItem value="pathway">Pathway</MenuItem>
                <MenuItem value="maintenance">Maintenance</MenuItem>
                <MenuItem value="reporting">Reporting</MenuItem>
                <MenuItem value="cleanup">Cleanup</MenuItem>
                <MenuItem value="notification">Notification</MenuItem>
                <MenuItem value="other">Other</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Cron Expression"
              value={jobForm.schedule.cronExpression}
              onChange={(e) => setJobForm({
                ...jobForm,
                schedule: { ...jobForm.schedule, cronExpression: e.target.value }
              })}
              fullWidth
              helperText="e.g., '0 2 * * *' for 2 AM daily"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={jobForm.schedule.timezone}
                onChange={(e) => setJobForm({
                  ...jobForm,
                  schedule: { ...jobForm.schedule, timezone: e.target.value }
                })}
                label="Timezone"
              >
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="Europe/London">Europe/London</MenuItem>
                <MenuItem value="Europe/Dublin">Europe/Dublin</MenuItem>
                <MenuItem value="America/New_York">America/New_York</MenuItem>
                <MenuItem value="America/Los_Angeles">America/Los_Angeles</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Endpoint URI"
              value={jobForm.endpoint.uri}
              onChange={(e) => setJobForm({
                ...jobForm,
                endpoint: { ...jobForm.endpoint, uri: e.target.value }
              })}
              fullWidth
              helperText="Full URL to trigger (e.g., https://api.example.com/api/pathways/auto-transition/trigger)"
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>HTTP Method</InputLabel>
              <Select
                value={jobForm.endpoint.method}
                onChange={(e) => setJobForm({
                  ...jobForm,
                  endpoint: { ...jobForm.endpoint, method: e.target.value }
                })}
                label="HTTP Method"
              >
                <MenuItem value="GET">GET</MenuItem>
                <MenuItem value="POST">POST</MenuItem>
                <MenuItem value="PUT">PUT</MenuItem>
                <MenuItem value="DELETE">DELETE</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Timeout (ms)"
              type="number"
              value={jobForm.settings.timeout}
              onChange={(e) => setJobForm({
                ...jobForm,
                settings: { ...jobForm.settings, timeout: parseInt(e.target.value) }
              })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              label="Max Retries"
              type="number"
              value={jobForm.settings.maxRetries}
              onChange={(e) => setJobForm({
                ...jobForm,
                settings: { ...jobForm.settings, maxRetries: parseInt(e.target.value) }
              })}
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={jobForm.settings.retryOnFailure}
                    onChange={(e) => setJobForm({
                      ...jobForm,
                      settings: { ...jobForm.settings, retryOnFailure: e.target.checked }
                    })}
                  />
                }
                label="Retry on Failure"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={jobForm.settings.notifyOnFailure}
                    onChange={(e) => setJobForm({
                      ...jobForm,
                      settings: { ...jobForm.settings, notifyOnFailure: e.target.checked }
                    })}
                  />
                }
                label="Notify on Failure"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={jobForm.settings.notifyOnSuccess}
                    onChange={(e) => setJobForm({
                      ...jobForm,
                      settings: { ...jobForm.settings, notifyOnSuccess: e.target.checked }
                    })}
                  />
                }
                label="Notify on Success"
              />
            </Box>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setJobDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleSaveJob} variant="contained" color="primary">
          {selectedJob ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render history dialog
  const renderHistoryDialog = () => (
    <Dialog open={historyDialogOpen} onClose={() => setHistoryDialogOpen(false)} maxWidth="lg" fullWidth>
      <DialogTitle>
        Run History: {selectedJob?.name}
      </DialogTitle>
      <DialogContent>
        {historyLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Run ID</TableCell>
                  <TableCell>Trigger</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Started</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Results</TableCell>
                  <TableCell>Error</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {jobHistory.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                        No run history available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  jobHistory.map((run) => (
                    <TableRow key={run.runId}>
                      <TableCell>
                        <Typography variant="caption" fontFamily="monospace">
                          {run.runId?.substring(0, 20)}...
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={run.trigger?.type || 'scheduled'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={run.status}
                          size="small"
                          color={getStatusColor(run.status)}
                        />
                      </TableCell>
                      <TableCell>{formatDate(run.startedAt)}</TableCell>
                      <TableCell>{formatDuration(run.durationMs)}</TableCell>
                      <TableCell>
                        {run.result && (
                          <Typography variant="caption">
                            {run.result.itemsProcessed !== undefined && `${run.result.itemsProcessed} processed`}
                            {run.result.itemsFailed > 0 && ` (${run.result.itemsFailed} failed)`}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {run.error?.message && (
                          <Tooltip title={run.error.message}>
                            <Typography variant="caption" color="error" noWrap sx={{ maxWidth: 200, display: 'block' }}>
                              {run.error.message}
                            </Typography>
                          </Tooltip>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setHistoryDialogOpen(false)}>Close</Button>
      </DialogActions>
    </Dialog>
  );

  // Render schedule dialog
  const renderScheduleDialog = () => (
    <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Schedule: {selectedJob?.name}</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <TextField
              label="Cron Expression"
              value={scheduleForm.cronExpression}
              onChange={(e) => setScheduleForm({ ...scheduleForm, cronExpression: e.target.value })}
              fullWidth
              helperText="Minute Hour Day Month Weekday (e.g., '0 2 * * *' for 2:00 AM daily)"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth>
              <InputLabel>Timezone</InputLabel>
              <Select
                value={scheduleForm.timezone}
                onChange={(e) => setScheduleForm({ ...scheduleForm, timezone: e.target.value })}
                label="Timezone"
              >
                <MenuItem value="UTC">UTC</MenuItem>
                <MenuItem value="Europe/London">Europe/London</MenuItem>
                <MenuItem value="Europe/Dublin">Europe/Dublin</MenuItem>
                <MenuItem value="America/New_York">America/New_York</MenuItem>
                <MenuItem value="America/Los_Angeles">America/Los_Angeles</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Human-Readable Description"
              value={scheduleForm.humanReadable}
              onChange={(e) => setScheduleForm({ ...scheduleForm, humanReadable: e.target.value })}
              fullWidth
              helperText="Optional description (e.g., 'Every day at 2:00 AM')"
            />
          </Grid>
          <Grid item xs={12}>
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>Common Cron Patterns:</strong><br />
                • <code>0 2 * * *</code> - Every day at 2:00 AM<br />
                • <code>0 3 * * 0</code> - Every Sunday at 3:00 AM<br />
                • <code>0 0 1 * *</code> - First of every month at midnight<br />
                • <code>*/15 * * * *</code> - Every 15 minutes
              </Typography>
            </Alert>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setScheduleDialogOpen(false)}>Cancel</Button>
        <Button onClick={handleUpdateSchedule} variant="contained" color="primary">
          Update Schedule
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Render confirm dialog
  const renderConfirmDialog = () => (
    <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)}>
      <DialogTitle>Confirm {confirmAction.type}</DialogTitle>
      <DialogContent>
        <Typography>
          Are you sure you want to {confirmAction.type} the job "{confirmAction.job?.name}"?
        </Typography>
        {confirmAction.type === 'delete' && (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This will also delete all run history for this job.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirmDialogOpen(false)}>Cancel</Button>
        <Button
          onClick={() => {
            if (confirmAction.type === 'delete') {
              handleDeleteJob(confirmAction.job.jobKey);
            }
          }}
          variant="contained"
          color="error"
        >
          {confirmAction.type}
        </Button>
      </DialogActions>
    </Dialog>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Scheduled Jobs Manager</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => { fetchJobs(); fetchSystemStatus(); }}
            variant="outlined"
          >
            Refresh
          </Button>
          <Button
            startIcon={<SyncIcon />}
            onClick={handleSyncAllJobs}
            variant="outlined"
            color="secondary"
          >
            Sync All to Cloud
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={handleInitializeDefaults}
            variant="outlined"
          >
            Initialize Defaults
          </Button>
          <Button
            startIcon={<AddIcon />}
            onClick={openCreateDialog}
            variant="contained"
            color="primary"
          >
            Add Job
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

      {renderSystemStatus()}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="All Jobs" />
          <Tab label="Active" />
          <Tab label="Paused / Disabled" />
        </Tabs>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {renderJobsTable()}
          </TabPanel>
          <TabPanel value={tabValue} index={1}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Schedule</TableCell>
                    <TableCell>Last Run</TableCell>
                    <TableCell>Next Run</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.filter(j => j.isEnabled && !j.isPaused).map((job) => (
                    <TableRow key={job.jobKey}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{job.name}</Typography>
                      </TableCell>
                      <TableCell>{job.schedule?.humanReadable || job.schedule?.cronExpression}</TableCell>
                      <TableCell>{formatDate(job.stats?.lastRunAt)}</TableCell>
                      <TableCell>{formatDate(job.stats?.nextScheduledRun)}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => handleTriggerJob(job.jobKey)}>
                          <PlayIcon fontSize="small" />
                        </IconButton>
                        <IconButton size="small" onClick={() => handlePauseJob(job.jobKey)}>
                          <PauseIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
          <TabPanel value={tabValue} index={2}>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Job</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Last Run</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobs.filter(j => !j.isEnabled || j.isPaused).map((job) => (
                    <TableRow key={job.jobKey}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">{job.name}</Typography>
                      </TableCell>
                      <TableCell>
                        {job.isPaused ? (
                          <Chip label="Paused" size="small" color="warning" />
                        ) : (
                          <Chip label="Disabled" size="small" color="default" />
                        )}
                      </TableCell>
                      <TableCell>{formatDate(job.stats?.lastRunAt)}</TableCell>
                      <TableCell align="right">
                        {job.isPaused ? (
                          <IconButton size="small" onClick={() => handleResumeJob(job.jobKey)}>
                            <PlayIcon fontSize="small" color="primary" />
                          </IconButton>
                        ) : (
                          <IconButton size="small" onClick={() => handleEnableJob(job.jobKey)}>
                            <PlayIcon fontSize="small" color="success" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </>
      )}

      {renderJobDialog()}
      {renderHistoryDialog()}
      {renderScheduleDialog()}
      {renderConfirmDialog()}
    </Box>
  );
};

export default ScheduledJobsManager;
